import * as joint from '@clientio/rappid';
import { TranslocoService } from '@jsverse/transloco';
import { SelectionType } from 'guideline/domain';
import { of, switchMap } from 'rxjs';
import { ComponentConverter } from 'shared/domain';

import {
  ActivityDefinitionDto,
  ComponentDto,
  FlowchartActionDto,
  FlowchartElementType,
  FlowchartLinkDto,
  GuidelineActionSignificance,
  ProposedComponentDto,
} from '@treetop/shared/model';

import { BasicNode } from './BasicNode';
import { IFlowchartNode } from './IFlowchartNode';
import { FlowchartSidebarStoreService } from '../../../../../guideline/feature-sidebar/src/lib/infrastructure/flowchart-sidebar-store.service';
import { FlowchartGenericDto } from '../infrastructure/base/flowchart-events.service';
import OperationsService from '../infrastructure/operations/operations.service';
import SubflowService from '../infrastructure/operations/subflow.service';

export class FlowchartActionNode extends BasicNode<FlowchartActionDto> implements IFlowchartNode {
  constructor(element: FlowchartActionDto) {
    super(element);
  }

  addOutgoingLink(link: FlowchartLinkDto): void {
    this.element.outgoingLinks = this.element.outgoingLinks || [];
    this.element.outgoingLinks.push(link);
  }

  removeOutgoingLink(linkIndex: number): void {
    this.element.outgoingLinks.splice(linkIndex, 1);
  }

  addToGraph(
    graph: joint.dia.Graph,
    paper: joint.dia.Paper,
    operationsService: OperationsService,
    subflowService: SubflowService,
    translocoService: TranslocoService,
    allowEvents = false,
  ): void {
    const title = this.getTitle(this.element, translocoService);
    const description = this.getDescription(this.element);

    const action = operationsService.actionNodeService.createActionNode(title, {
      x: this.element.positionX,
      y: this.element.positionY,
      tooltip: title,
      id: this.element.id,
      uniqueId: this.element.uniqueId,
      activityType: this.element.guidelineAction?.activityDefinition?.activityType,
      size: { width: this.element.width, height: this.element.height },
      colorPresetId: this.element.colorPresetId,
    });

    graph.addCell(action);
    operationsService.addElementTags(this.element);
    operationsService.setClickableForSmartbook(action.id, description);

    if (this.element?.showDescription) {
      if (description) {
        operationsService.initElementsDescription(this.element.id, description);
      }
    }
  }

  setPropertiesForInit(element: joint.dia.Cell): void {
    super.setPropertiesForInit(element);
    this.element.elementType = FlowchartElementType.FlowchartAction;
    if (element.attributes.isContainerSubComponent) {
      this.element.parentId = element.attributes.parentContainerId;
    }
  }

  async setPropertiesAfterSearch(
    definition: ComponentDto | ProposedComponentDto,
    medicationRequests: ActivityDefinitionDto[],
    componentConverter: ComponentConverter,
    selectedElementView?: joint.dia.ElementView,
    translocoService?: TranslocoService,
    flowchartSidebarStoreService?: FlowchartSidebarStoreService,
  ): Promise<void> {
    const copiedObject: FlowchartGenericDto = JSON.parse(JSON.stringify(this.element));

    if (selectedElementView) {
      copiedObject.width = selectedElementView.model.attributes.size.width;
      copiedObject.height = selectedElementView.model.attributes.size.height;
    }

    of(definition)
      .pipe(
        switchMap((definition) => {
          if (definition.code.startsWith('PC-')) {
            return of(componentConverter.convertProposedComponentToAction(definition));
          } else {
            return componentConverter.convertActivityToAction(definition, medicationRequests, this.element.uniqueId);
          }
        }),
      )
      .subscribe((action) => {
        copiedObject.guidelineAction = action;
        copiedObject.guidelineAction.significance = GuidelineActionSignificance.Required;

        if ('id' in action) {
          copiedObject.guidelineActionId = action.id;
        }

        if (copiedObject.guidelineAction.links?.length > 0) {
          copiedObject.links = [
            ...copiedObject.guidelineAction.links.map((link) => ({
              ...link,
              fromElementUniqueId: copiedObject.uniqueId,
            })),
          ];
          delete copiedObject.guidelineAction.links;
        }

        flowchartSidebarStoreService.setDto({
          element: copiedObject,
          type: SelectionType.Flowchart,
        });
      });
  }

  private getDescription(element: FlowchartActionDto): string {
    return element.guidelineAction?.description;
  }

  private getTitle(element: FlowchartActionDto, translocoService: TranslocoService): string {
    if (element.guidelineAction?.title) {
      return element.guidelineAction?.title;
    } else {
      return translocoService.translate('SetTitle');
    }
  }
}
